import { useState } from 'react';

import { css } from '@emotion/css';
import { Box, Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles';
import { userFormattedName } from '@/models/UserModel';
import { Queries } from '@/queries/Queries';
import { PatientRenewal } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx';

import { Button } from '../../atoms/Button';
import { InformationPaper } from '../../atoms/InformationPaper';

export const NurseReinclusionRequestBanner = ({
  patientId,
}: {
  patientId: string;
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const deleteNurseReinclusionRequestMutation =
    Queries.practitioner.useDeleteNurseReinclusionSuggestion();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reinclusionNudge =
    Queries.practitioner.useGetNurseReinclusionSuggestion(patientId);

  if (!reinclusionNudge?.data?.reinclusion_request_active) {
    return null;
  }

  const reinclusion = reinclusionNudge.data;

  return (
    <InformationPaper className={styles.container} isSquare variant="outlined">
      <div className={styles.leftContainer}>
        {reinclusion.nurse_profile_picture && (
          <Box
            className={styles.picture}
            src={reinclusion.nurse_profile_picture.url}
            component="img"
            alt="nurse-picture"
          />
        )}
        <Typography variant="subtitle">
          {reinclusion.nurse_first_name && reinclusion.nurse_last_name
            ? t('patientNavigation.reinclusion.doctorTitleWithNurseName', {
                nurseName: userFormattedName(
                  reinclusion.nurse_last_name,
                  reinclusion.nurse_first_name,
                ),
              })
            : t('patientNavigation.reinclusion.doctorTitle')}
        </Typography>
      </div>
      <div className={styles.ctas}>
        <Button
          variant="contained"
          onClick={() => setIsModalOpen(true)}
          id="accept-reinclusion-request"
        >
          {t('patientNavigation.reinclusion.doctorCTAAccept')}
        </Button>
        <PatientRenewal
          patientId={patientId}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={() => setIsModalOpen(false)}
        />
        <Button
          id="deny-reinclusion-request"
          pending={deleteNurseReinclusionRequestMutation.isPending}
          variant="contained"
          onClick={() =>
            deleteNurseReinclusionRequestMutation.mutate(patientId)
          }
        >
          {t('patientNavigation.reinclusion.doctorCTADeny')}
        </Button>
      </div>
    </InformationPaper>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(8, 12)};
  `,
  ctas: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(4)};
  `,
  picture: css`
    width: 40px;
    height: 40px;
    border-radius: 50px;
  `,
  leftContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(4)};
  `,
});
